import {
  IUpsGroupingRequest,
  IUpsGroupingRequestItem,
  IUpsGroupingReturnAddress,
  IUpsGroupingRlcReturn,
  IUpsGroupingSenderAddress,
  PolicyValidatorAddress,
} from '@src/models/api';
import { IReturnShipments } from '@src/stores/returnStore';
import {
  ItemToReturn,
  OrderDetailsSessionDataV2,
  ShippingInfoDict,
} from '@src/models/returns';
import { ShipFromAddress, ShipToAddress } from '@src/models/finalize-return';
import { RETURN_METHOD } from '@src/constants/general';
import { AdaptedReturnMethods } from './eligibility-utils';
import { getSsReturnCreateShipFromAddress } from '.';

interface IBuildUpsGroupingRequest {
  orderDetailsV2: OrderDetailsSessionDataV2;
  selectedItems: ItemToReturn[];
  adaptedMethods: AdaptedReturnMethods;
  deliveryDictionary?: ShippingInfoDict;
}

/**
 * The return destination for one line, as the shipping service names it.
 *
 * `name` has no source of its own: the methods response gives a contact name
 * and nothing else, and this is a return destination rather than the shopper,
 * so it may not be filled from order or customer data. The backend's guidance
 * is to repeat the contact name, which is what happens here.
 */
const toGroupingReturnAddress = (
  address: ShipToAddress,
): IUpsGroupingReturnAddress => ({
  name: address.contactName,
  contactName: address.contactName,
  addressLine1: address.addressLine1,
  addressLine2: address.addressLine2,
  city: address.city,
  state: address.state,
  zipCode: address.zipCode,
  phone: address.dayPhone,
});

/**
 * Where the parcel leaves from. One origin serves the whole drop-off selection,
 * matching how the create request resolves it — the point is that both calls
 * describe the same origin, not that each line gets the most precise one.
 */
const toGroupingSenderAddress = (
  address: ShipFromAddress,
): IUpsGroupingSenderAddress => ({
  firstName: address.firstName || '',
  lastName: address.lastName || '',
  email: address.emailId || '',
  addressLine1: address.addressLine1 || '',
  addressLine2: address.addressLine2,
  city: address.city || '',
  state: address.state || '',
  zipCode: address.zipCode || '',
});

/**
 * Why no request was built. Both mean "do not call the service", but they are
 * not interchangeable and must not be collapsed: one is an ordinary return with
 * no parcel leg, and the other is a return that needs grouping and cannot be
 * grouped. Treating the second as the first advances the customer with no floor.
 */
export type UpsGroupingRequestOutcome =
  | { status: 'ready'; request: IUpsGroupingRequest }
  | { status: 'noParcelLeg' }
  | { status: 'destinationMissing' }
  | { status: 'originMissing' };

/**
 * Builds the grouping request for the parcel drop-off leg of a return.
 */
export const buildUpsGroupingRequest = ({
  orderDetailsV2,
  selectedItems,
  adaptedMethods,
  deliveryDictionary,
}: IBuildUpsGroupingRequest): UpsGroupingRequestOutcome => {
  const dropOffItems = selectedItems.filter(
    (item) => item.returnMethod === RETURN_METHOD.UPS_DROP_OFF,
  );

  // A return with no parcel leg is not grouped at all. Pickup and in-store legs
  // are decided elsewhere, and a return split across methods asks about the
  // drop-off items once rather than once per method.
  if (dropOffItems.length === 0) return { status: 'noParcelLeg' };

  // Paired up first so the destination is narrowed rather than asserted, and so
  // a missing one is a length mismatch below rather than a hole in the output.
  const linesWithDestination = dropOffItems.flatMap((item) => {
    const returnAddress =
      adaptedMethods[item.lineItemId]?.methods[RETURN_METHOD.UPS_DROP_OFF]
        ?.shipToAddress;
    return returnAddress ? [{ item, returnAddress }] : [];
  });

  // Grouping is by destination, so a line whose destination is missing cannot
  // be placed. Abandoning the whole request is deliberate: dropping the line
  // instead would return a grouping that silently describes a different return
  // from the one the customer is making.
  if (linesWithDestination.length !== dropOffItems.length) {
    return { status: 'destinationMissing' };
  }

  // Resolved once from the whole drop-off selection, which is what the create
  // call does. Resolving it per line instead looks more precise and is not: the
  // helper picks the ship-to-home line out of the list it is given, so a list of
  // one is just that line, and a store-fulfilled line has no shipping address to
  // find. The two calls would then describe different origins for the same
  // lines, and the service is entitled to group them differently as a result.
  const senderAddress = toGroupingSenderAddress(
    getSsReturnCreateShipFromAddress(
      orderDetailsV2.emailId,
      dropOffItems,
      deliveryDictionary,
    ),
  );

  // An origin the service cannot ship from is as disqualifying as a destination
  // it cannot ship to, and nothing downstream would catch it: the empty strings
  // this coerces to are a well-formed request describing nowhere.
  if (!senderAddress.addressLine1 || !senderAddress.zipCode) {
    return { status: 'originMissing' };
  }

  const shippingRequests: IUpsGroupingRequestItem[] = linesWithDestination.map(
    ({ item, returnAddress }) => ({
      lineNumber: item.lineItemId,
      poNumber: item.poNumber || '',
      // Multiplied here because the shipping service cannot do it: its line
      // carries no quantity, so it sums exactly what it is given. The same
      // product must reach it again at submit, or it recomputes a higher floor
      // there than the one shown at review and the customer is issued more
      // labels than they chose.
      totalWeight: (item.itemWeight || 0) * (item.returnQuantity || 0),
      description: item.itemDescription || '',
      // Nothing reaching this request can be a disposal line: items needing
      // disposal are not offered parcel drop-off in the first place. If that
      // ever stops being true this needs a real source rather than a constant.
      destroy: false,
      return: toGroupingReturnAddress(returnAddress),
      sender: senderAddress,
    }),
  );

  return {
    status: 'ready',
    request: {
      orderNumber: orderDetailsV2.orderNumber,
      shippingRequests,
    },
  };
};

/**
 * Names the way an answer fails to account for the selection, or undefined when
 * it accounts for it exactly.
 *
 * A non-empty answer is not the same as a complete one. Every selected drop-off
 * line has to appear once and nothing else may appear, because the legs built
 * from this replace the drop-off legs wholesale: a line the answer omits is
 * dropped from the return altogether, and one it names twice is submitted
 * twice. The methods response is checked the same way and for the same reason.
 *
 * Suites are checked for the same completeness, one step removed from the
 * lines: they are what the box counts are keyed to on the create request, so a
 * suite named twice costs the customer a count rather than an item.
 */
export const describeGroupingMismatch = (
  rlcReturns: IUpsGroupingRlcReturn[],
  selectedItems: ItemToReturn[],
):
  | 'repeated_line'
  | 'repeated_suite'
  | 'missed_line'
  | 'unexpected_line'
  | undefined => {
  const expected = new Set(
    selectedItems
      .filter((item) => item.returnMethod === RETURN_METHOD.UPS_DROP_OFF)
      .map((item) => item.lineItemId),
  );
  const named = rlcReturns.flatMap((group) => group.lineNumbers);

  // Counted before the set collapses them, or a line named twice while another
  // is missing would pass as the right size.
  if (new Set(named).size !== named.length) return 'repeated_line';

  // A suite is a group's only identifier, and the create request keys the
  // customer's box count to it. Two groups sharing one collapse onto a single
  // count, so the count set on the other is dropped with nothing to signal it
  // — the same class of silent loss as a repeated line, arriving by a
  // different route.
  const suites = rlcReturns.map((group) => group.rlcReturnSuite);
  if (new Set(suites).size !== suites.length) return 'repeated_suite';
  if (named.some((lineItemId) => !expected.has(lineItemId))) {
    return 'unexpected_line';
  }
  if (named.length !== expected.size) return 'missed_line';
  return undefined;
};

/**
 * The box counts the customer has already chosen, keyed by the suite each was
 * chosen for.
 *
 * Collected before an edit re-runs the calls, because by the time the fresh
 * grouping arrives the suite ids are gone: rebuilding the legs for the new
 * selection clears the suite along with the floor, so a leg reaching the
 * grouping call no longer says which group its count was entered against.
 * Reading the pair while they are still on the same leg is the only point at
 * which the count and its suite are both known.
 */
export type ChosenBoxCounts = Record<string, number>;

/**
 * Takes the counts the customer has entered off the legs as they stand.
 *
 * Only drop-off legs carry a count, and only a leg still naming its suite can
 * contribute one — a leg whose suite has already been cleared has a count with
 * nothing to key it to, and guessing which new group inherits it is exactly the
 * mis-keying the suite ids exist to prevent.
 *
 * Keying on the suite across two separate calls is safe, and worth saying why
 * because the whole of this depends on it: the suite is not an id the service
 * mints per request. It is read out of the destination address we send — the
 * service requires the suite to be in its second address line, and groups by
 * it — so the same address yields the same suite every time. A suite that does
 * change between two calls changes because the destination did, and a count
 * entered for the old destination is not a count for the new one.
 */
export const collectChosenBoxCounts = (
  shipments: IReturnShipments[] = [],
): ChosenBoxCounts =>
  Object.fromEntries(
    shipments.flatMap((shipment) =>
      shipment.shipmentType === 'DROPOFF' &&
      shipment.rlcReturnSuite &&
      shipment.boxQuantity !== undefined
        ? [[shipment.rlcReturnSuite, shipment.boxQuantity] as const]
        : [],
    ),
  );

/**
 * Widens an adapted per-method address to the shape a shipment leg carries.
 *
 * `shipToName` and `emailId` come out blank, and that is worth stating because
 * the create request carries this address through verbatim: today the validator
 * supplies a facility name and a returns mailbox for them, and under the new
 * contract nothing does.
 *
 * They are not fields that could be filled in from elsewhere. Both describe the
 * destination — the name of the returns centre and its mailbox — so the order's
 * email and the customer's name are the wrong values, not merely unavailable
 * ones, and nothing on the frontend maps a suite or address to a facility. The
 * service that owns the return destination is the only place they can come
 * from, so blank here is the honest answer until that contract carries them.
 *
 * The two fields the create validator does check, contact name and day phone,
 * both arrive on the methods response and are populated.
 */
const toLegAddress = (
  address?: ShipToAddress,
): PolicyValidatorAddress | undefined =>
  address && {
    ...address,
    addressLine2: address.addressLine2 || '',
    emailId: address.emailId || '',
  };

/**
 * Rebuilds the parcel drop-off legs from the grouping the shipping service
 * returned, and leaves every other leg alone.
 *
 * The groups are taken as given rather than reconciled with what was already
 * there: the service decides which lines travel together, and a leg rebuilt
 * from a fresh answer cannot inherit a grouping the answer disagrees with.
 *
 * The box count is the one thing that can survive, and only when the caller
 * hands over the counts already chosen. On the way to the box step there is
 * nothing to survive — the customer has not been asked yet — so the count is
 * seeded from the floor and `chosenCounts` is left empty. On a review-screen
 * edit they have already chosen, and a count they raised deliberately is theirs
 * rather than the service's to reset: a suite that survives the edit keeps its
 * count, lifted to the new floor if the floor has passed it. A suite the answer
 * has not named before is new, and seeds from its floor like any other.
 *
 * Whether the answer accounts for the selection is settled before this is
 * called — see describeGroupingMismatch. The line filter here is a last
 * backstop against building a leg around a line the methods response never
 * described, not the completeness guarantee itself; it cannot tell a line
 * selected for pickup from one selected for drop-off.
 *
 * What each line carries is not the service's to decide, only which lines
 * travel together — so the per-line values already held for a line are carried
 * across unchanged. The return token, disposition code and policy id are still
 * read straight off the shipment when the create request is built, and a line
 * rebuilt without them would submit blanks.
 */
export const applyGroupingToShipments = (
  rlcReturns: IUpsGroupingRlcReturn[],
  currentShipments: IReturnShipments[] = [],
  adaptedMethods: AdaptedReturnMethods,
  chosenCounts: ChosenBoxCounts = {},
): IReturnShipments[] => {
  // Across every leg rather than the drop-off ones alone: these values are
  // per line and say nothing about the method, so wherever a line sits now is
  // where its values are.
  const heldLineValues = new Map(
    currentShipments.flatMap((shipment) =>
      shipment.lineItems.map(
        (lineItem) => [lineItem.lineItemId, lineItem] as const,
      ),
    ),
  );

  const groupedLegs = rlcReturns.flatMap((group) => {
    const lineItems = group.lineNumbers
      .filter((lineItemId) => adaptedMethods[lineItemId])
      .map((lineItemId) => ({
        ...heldLineValues.get(lineItemId),
        lineItemId,
        itemEligibility: Boolean(
          adaptedMethods[lineItemId]?.methods[RETURN_METHOD.UPS_DROP_OFF]
            ?.eligible,
        ),
      }));
    if (lineItems.length === 0) return [];

    // The response identifies a group by suite and carries no address, so the
    // address is correlated back from what was sent for the lines in it.
    const shipToAddress = toLegAddress(
      adaptedMethods[lineItems[0].lineItemId]?.methods[
        RETURN_METHOD.UPS_DROP_OFF
      ]?.shipToAddress,
    );

    return [
      {
        shipmentType: 'DROPOFF' as const,
        shipToAddress,
        lineItems,
        // Kept on the leg so the chosen box count can be returned to its suite
        // by identity later, rather than by re-deriving which lines belong to
        // which group from a grouping that may since have gone stale.
        rlcReturnSuite: group.rlcReturnSuite,
        // The floor is the service's number and nothing else may set it: the
        // customer may raise their count but never lower it past what the
        // service asked for.
        minBoxes: group.boxQuantity,
        // Which leaves the floor as the starting count unless the customer had
        // already chosen a higher one for this same suite, in which case theirs
        // stands. Taking the larger of the two rather than trusting either on
        // its own is what satisfies both halves at once — a raised floor lifts
        // a count that has fallen below it, and a raised count survives a floor
        // that has not.
        boxQuantity: Math.max(
          chosenCounts[group.rlcReturnSuite] ?? group.boxQuantity,
          group.boxQuantity,
        ),
      },
    ];
  });

  const otherLegs = currentShipments.filter(
    (shipment) => shipment.shipmentType !== 'DROPOFF',
  );

  // Numbered in one pass over the merged list. Numbering the two sets
  // separately can hand two legs the same shipmentId, which the field-update
  // helpers key on.
  return [...groupedLegs, ...otherLegs].map((leg, index) => ({
    ...leg,
    shipmentId: index + 1,
  }));
};
